import React, { useEffect, useRef, useState } from 'react';
import useSEO from '../HireWithUs/Pages/HomePage/useSEO';
import Header from '../HireWithUs/Pages/Header&Footer/Header';
import Footer from '../HireWithUs/Pages/Header&Footer/Footer';
import { ArrowDown, ArrowRight, Briefcase, ClipboardCheck, Clock, GraduationCap, Heart, Quote, Star, Target, TrendingUp, Users, Zap } from 'lucide-react';
import { assetSrc, caseStudyMetrics, DEMO_METRICS_NOTE } from './caseStudyContent';
import CaseStudyDemo from './CaseStudyDemos';
import GrandGamingHero from './GrandGamingHero';
import GrandGamingStudy from './GrandGamingStudy';
import ManestStudy from './ManestStudy';
import CustomerInsights from './CustomerInsights';
import CaseStudyChallenge from './CaseStudyChallenge';

const CTA_HREF = '/hirewithus/book-demo';
const CTA_LABEL = 'Experience SearchJob.ai with us';

const primaryButton =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring';

function usePointerNudge() {
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!fine || reduce) return undefined;
    const move = (event) => {
      const rect = node.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width - 0.5) * 10;
      const y = ((event.clientY - rect.top) / rect.height - 0.5) * 8;
      node.style.setProperty('--px', `${Math.max(-5, Math.min(5, x)).toFixed(2)}px`);
      node.style.setProperty('--py', `${Math.max(-4, Math.min(4, y)).toFixed(2)}px`);
    };
    const leave = () => {
      node.style.setProperty('--px', '0px');
      node.style.setProperty('--py', '0px');
    };
    node.addEventListener('pointermove', move);
    node.addEventListener('pointerleave', leave);
    return () => {
      node.removeEventListener('pointermove', move);
      node.removeEventListener('pointerleave', leave);
    };
  }, []);

  return ref;
}

function Section({ children, divided = true, compact = false, className = '' }) {
  return (
    <section className={`cs-reveal ${divided ? 'border-b border-border' : ''}`}>
      <div
        className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${
          className || (compact ? 'py-10 sm:py-12 lg:py-14' : 'py-12 lg:py-16')
        }`}
      >
        {children}
      </div>
    </section>
  );
}

const approachCardIcons = {
  target: Target,
  heart: Heart,
  users: Users,
};

function KinzyApproach({ study }) {
  const { title, body } = study.approach;
  const cards = study.approach.cards || (study.approach.items || []).map((item) => ({
    title: item,
    body: '',
    icon: 'target',
  }));

  return (
    <Section className="py-12 sm:py-14 lg:py-16">
      <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <div className="min-w-0">
          <SectionLabel>The SearchJob.ai approach</SectionLabel>
          <h2 className="mt-3 max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">
            {title}
          </h2>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-muted-foreground">{body}</p>
        </div>
        <div className="relative min-w-0">
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute -right-4 top-1/2 h-[118%] w-[78%] -translate-y-1/2 text-primary"
            viewBox="0 0 320 380"
            fill="none"
          >
            <circle cx="230" cy="64" r="48" stroke="currentColor" strokeOpacity="0.14" />
            <circle cx="168" cy="188" r="78" stroke="currentColor" strokeOpacity="0.1" />
            <circle cx="248" cy="308" r="40" stroke="currentColor" strokeOpacity="0.12" />
            <path d="M230 64L168 188L248 308" stroke="currentColor" strokeOpacity="0.1" />
            <circle cx="230" cy="64" r="2.5" fill="currentColor" fillOpacity="0.4" />
            <circle cx="168" cy="188" r="2.5" fill="currentColor" fillOpacity="0.32" />
            <circle cx="248" cy="308" r="2" fill="currentColor" fillOpacity="0.36" />
          </svg>
          <ul className="relative grid gap-3">
            {cards.map((item) => {
              const Icon = approachCardIcons[item.icon];
              return (
                <li
                  key={item.title}
                  className="rounded-2xl border border-border bg-card shadow-sm px-4 py-4 sm:px-5"
                  style={{ boxShadow: '0 0 28px hsl(158 40% 72% / 0.07)' }}
                >
                  <div className="flex items-start gap-3">
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold leading-snug text-foreground">{item.title}</p>
                      {item.body ? <p className="mt-1 text-sm leading-snug text-muted-foreground">{item.body}</p> : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </Section>
  );
}

const flowNodeStyle = {
  mint: {
    borderColor: 'hsl(158 40% 72% / 0.9)',
    boxShadow: '0 0 8px hsl(158 40% 72% / 0.45)',
  },
  gold: {
    borderColor: 'hsl(40 58% 64% / 0.95)',
    boxShadow: '0 0 8px hsl(40 58% 64% / 0.4)',
  },
  green: {
    borderColor: 'hsl(152 46% 56% / 0.95)',
    boxShadow: '0 0 8px hsl(152 46% 56% / 0.42)',
  },
};

const flowCardStyle = {
  mint: {
    borderColor: 'hsl(158 40% 72% / 0.34)',
    backgroundColor: 'hsl(40 20% 99%)',
    boxShadow: '0 0 16px hsl(158 40% 72% / 0.09)',
  },
  gold: {
    borderColor: 'hsl(40 52% 60% / 0.58)',
    backgroundColor: 'hsl(40 40% 97%)',
    boxShadow: '0 0 18px hsl(40 55% 58% / 0.13)',
  },
  green: {
    borderColor: 'hsl(152 45% 46% / 0.78)',
    backgroundColor: 'hsl(152 35% 96%)',
    boxShadow: '0 0 20px hsl(152 45% 46% / 0.16)',
  },
};

const flowLabelClass = {
  mint: 'text-primary',
  gold: 'text-[hsl(32_55%_32%)]',
  green: 'text-[hsl(152_40%_28%)]',
};

function FlowNode({ tone = 'mint' }) {
  return (
    <span
      aria-hidden="true"
      className="relative z-20 block h-2 w-2 shrink-0 rounded-full border bg-background"
      style={flowNodeStyle[tone]}
    />
  );
}

function FlowCard({ tone, children }) {
  return (
    <div
      className="relative z-10 flex h-[4.5rem] w-full items-center justify-center gap-1.5 rounded-xl border px-2.5 text-center text-sm font-semibold leading-snug text-foreground"
      style={flowCardStyle[tone]}
    >
      {children}
    </div>
  );
}

const flowGroupFrame = {
  mint: {
    borderColor: 'hsl(158 40% 72% / 0.28)',
    boxShadow: '0 0 28px hsl(158 40% 72% / 0.07)',
    rule: 'hsl(158 40% 72% / 0.35)',
  },
  gold: {
    borderColor: 'hsl(40 52% 60% / 0.5)',
    boxShadow: '0 0 24px hsl(40 55% 58% / 0.1)',
    rule: 'hsl(40 58% 64% / 0.4)',
  },
  green: {
    borderColor: 'hsl(152 45% 46% / 0.55)',
    boxShadow: '0 0 24px hsl(152 45% 46% / 0.1)',
    rule: 'hsl(152 46% 56% / 0.42)',
  },
};

function FlowGroup({ tone, label, children, bandClassName = 'h-[5.25rem] items-center' }) {
  const frame = flowGroupFrame[tone];
  return (
    <div
      className="relative z-10 flex h-full min-w-0 flex-col rounded-2xl border"
      style={{ borderColor: frame.borderColor, boxShadow: frame.boxShadow }}
    >
      <div className="flex h-11 shrink-0 items-center gap-2.5 px-3">
        <span aria-hidden="true" className="h-px min-w-2 flex-1" style={{ backgroundColor: frame.rule }} />
        <p className={`shrink-0 whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.14em] ${flowLabelClass[tone]}`}>
          {label}
        </p>
        <span aria-hidden="true" className="h-px min-w-2 flex-1" style={{ backgroundColor: frame.rule }} />
      </div>
      <div className={`flex px-2.5 ${bandClassName}`}>{children}</div>
    </div>
  );
}

function BandWorkflow({ study }) {
  const bands = study.workflowBands;
  const [lead, middle, outcome] = bands;
  const desktopColumns = 'minmax(0,4fr) 1.25rem minmax(11.5rem,1.25fr) 1.25rem minmax(11.5rem,1.3fr)';
  const leadColumns = lead.steps.map(() => 'minmax(0,1fr)').join(' 1rem ');
  const hot = study.workflowHighlight;
  const stepClass = (step) => (step === hot ? 'band-step band-hot' : 'band-step');
  return (
    <Section compact>
      <SectionLabel>How the workflow changed</SectionLabel>
      <SectionTitle>{study.workflowTitle}</SectionTitle>
      {study.workflowIntro ? (
        <p className="mt-3 w-full max-w-none text-base leading-relaxed text-muted-foreground">{study.workflowIntro}</p>
      ) : null}
      <div className="relative mt-8 space-y-4 lg:hidden">
        {bands.map((band) => (
          <FlowGroup key={band.label} tone={band.tone} label={band.label} bandClassName="flex-col gap-3 py-3">
            {band.steps.map((step) => (
              <FlowCard key={step} tone={band.tone} className={stepClass(step)}>
                {step}
              </FlowCard>
            ))}
          </FlowGroup>
        ))}
      </div>
      <div className="relative mt-8 hidden lg:block">
        <div className="grid items-stretch" style={{ gridTemplateColumns: desktopColumns }}>
          <FlowGroup tone={lead.tone} label={lead.label}>
            <div className="grid h-full w-full items-center" style={{ gridTemplateColumns: leadColumns }}>
              {lead.steps.map((step, index) => (
                <React.Fragment key={step}>
                  <FlowCard tone={lead.tone} className={stepClass(step)}>{step}</FlowCard>
                  {index < lead.steps.length - 1 ? (
                    <span className="flex items-center justify-center"><FlowNode /></span>
                  ) : null}
                </React.Fragment>
              ))}
            </div>
          </FlowGroup>
          <div className="flex flex-col" aria-hidden="true">
            <div className="h-11" />
            <div className="flex h-[5.25rem] items-center justify-center"><FlowNode tone={middle.tone} /></div>
          </div>
          <FlowGroup tone={middle.tone} label={middle.label}>
            <FlowCard tone={middle.tone} className={stepClass(middle.steps[0])}>{middle.steps[0]}</FlowCard>
          </FlowGroup>
          <div className="flex flex-col" aria-hidden="true">
            <div className="h-11" />
            <div className="flex h-[5.25rem] items-center justify-center"><FlowNode tone={outcome.tone} /></div>
          </div>
          <FlowGroup tone={outcome.tone} label={outcome.label}>
            <FlowCard tone={outcome.tone} className={stepClass(outcome.steps[0])}>
              {outcome.steps[0]}
            </FlowCard>
          </FlowGroup>
        </div>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-0 right-0 z-0 h-px"
          style={{
            top: '5.375rem',
            background:
              'linear-gradient(to right, hsl(158 40% 72% / 0.55) 0%, hsl(158 40% 72% / 0.55) 62%, hsl(40 58% 64% / 0.6) 78%, hsl(152 46% 56% / 0.7) 100%)',
          }}
        >
          <span className="kinzy-flow-dot" />
        </div>
        <style>{`
          .band-step { transition: transform 200ms ease, border-color 200ms ease; }
          .band-hot { box-shadow: 0 0 0 1px hsl(165 37% 22% / 0.28); }
          @media (hover: hover) and (pointer: fine) {
            .band-step:hover { transform: translateY(-2px); }
          }
          .kinzy-flow-dot {
            position: absolute;
            width: 7px;
            height: 7px;
            border-radius: 9999px;
            background: hsl(165 37% 22%);
            box-shadow: 0 0 10px 3px hsl(165 37% 22% / 0.45);
            top: 50%;
            left: 0;
            transform: translate(-50%, -50%);
            animation: kinzy-flow-dot 8s ease-in-out infinite;
          }
          @keyframes kinzy-flow-dot {
            0% { left: 0%; opacity: 0; }
            8% { opacity: 1; }
            92% { opacity: 1; }
            100% { left: 100%; opacity: 0; }
          }
          @media (prefers-reduced-motion: reduce) {
            .kinzy-flow-dot { animation: none; opacity: 0; }
            .band-step { transition: none; }
          }
        `}</style>
      </div>
      {study.workflowNote?.body ? (
        <div className="mt-6 max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">{study.workflowNote.label}</p>
          <p className="mt-2 text-base leading-relaxed text-muted-foreground">{study.workflowNote.body}</p>
        </div>
      ) : null}
    </Section>
  );
}

function KinzyWorkflow({ study }) {
  if (study.workflowBands) {
    return <BandWorkflow study={study} />;
  }
  const aiSteps = study.workflow.filter((step) => step !== 'Human review' && step !== 'Hired');
  const desktopColumns = 'minmax(0,4fr) 1.25rem minmax(11.5rem,1.25fr) 1.25rem minmax(11.5rem,1.3fr)';
  const referenceFlow = study.workflow.includes('Human review') && study.workflow.includes('Hired') && aiSteps.length === 4;

  if (!referenceFlow) {
    return (
      <Section compact>
        <SectionLabel>How the workflow changed</SectionLabel>
        <SectionTitle>{study.workflowTitle}</SectionTitle>
        {study.workflowIntro ? (
          <p className="mt-3 w-full max-w-none text-base leading-relaxed text-muted-foreground">{study.workflowIntro}</p>
        ) : null}
        <ol className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex">
          {study.workflow.map((step, index) => (
            <li key={step} className="cs-stagger min-w-0 flex-1" style={{ '--cs-i': index }}>
              <FlowCard tone={step === 'Shortlist' ? 'green' : 'mint'}>
                {step}
              </FlowCard>
            </li>
          ))}
        </ol>
        {study.workflowNote?.body ? (
          <div className="mt-6 max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">{study.workflowNote.label}</p>
            <p className="mt-2 text-base leading-relaxed text-muted-foreground">{study.workflowNote.body}</p>
          </div>
        ) : null}
      </Section>
    );
  }

  return (
    <Section compact>
      <SectionLabel>How the workflow changed</SectionLabel>
      <SectionTitle>{study.workflowTitle}</SectionTitle>
      <p className="mt-3 w-full max-w-none text-base leading-relaxed text-muted-foreground">{study.workflowIntro}</p>

      <div className="relative mt-8 space-y-4 lg:hidden">
        <FlowGroup tone="mint" label="AI-led workflow" bandClassName="flex-col gap-3 py-3">
          {aiSteps.map((step) => (
            <FlowCard key={step} tone="mint">{step}</FlowCard>
          ))}
        </FlowGroup>
        <FlowGroup tone="gold" label="Human judgment" bandClassName="items-center py-3">
          <FlowCard tone="gold">Human review</FlowCard>
        </FlowGroup>
        <FlowGroup tone="green" label="Outcome" bandClassName="items-center py-3">
          <FlowCard tone="green">
            Hired
          </FlowCard>
        </FlowGroup>
      </div>

      <div className="relative mt-8 hidden lg:block">
        <div className="grid items-stretch" style={{ gridTemplateColumns: desktopColumns }}>
          <FlowGroup tone="mint" label="AI-led workflow">
            <div
              className="grid h-full w-full items-center"
              style={{ gridTemplateColumns: 'minmax(0,1fr) 1rem minmax(0,1fr) 1rem minmax(0,1fr) 1rem minmax(0,1fr)' }}
            >
              {aiSteps.map((step, index) => (
                <React.Fragment key={step}>
                  <FlowCard tone="mint">{step}</FlowCard>
                  {index < aiSteps.length - 1 ? (
                    <span className="flex items-center justify-center">
                      <FlowNode />
                    </span>
                  ) : null}
                </React.Fragment>
              ))}
            </div>
          </FlowGroup>
          <div className="flex flex-col" aria-hidden="true">
            <div className="h-11" />
            <div className="flex h-[5.25rem] items-center justify-center">
              <FlowNode tone="gold" />
            </div>
          </div>
          <FlowGroup tone="gold" label="Human judgment">
            <FlowCard tone="gold">Human review</FlowCard>
          </FlowGroup>
          <div className="flex flex-col" aria-hidden="true">
            <div className="h-11" />
            <div className="flex h-[5.25rem] items-center justify-center">
              <FlowNode tone="green" />
            </div>
          </div>
          <FlowGroup tone="green" label="Outcome">
            <FlowCard tone="green">
              Hired
            </FlowCard>
          </FlowGroup>
        </div>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-0 right-0 z-0 h-px"
          style={{
            top: '5.375rem',
            background:
              'linear-gradient(to right, hsl(158 40% 72% / 0.55) 0%, hsl(158 40% 72% / 0.55) 62%, hsl(40 58% 64% / 0.6) 78%, hsl(152 46% 56% / 0.7) 100%)',
          }}
        >
          <span className="kinzy-flow-dot" />
        </div>
      </div>

      <style>{`
        .kinzy-flow-dot,
        .kinzy-flow-dot-v {
          position: absolute;
          width: 7px;
          height: 7px;
          border-radius: 9999px;
          background: hsl(165 37% 22%);
          box-shadow: 0 0 10px 3px hsl(165 37% 22% / 0.45);
        }
        .kinzy-flow-dot {
          top: 50%;
          left: 0;
          transform: translate(-50%, -50%);
          animation: kinzy-flow-dot 8s ease-in-out infinite;
        }
        .kinzy-flow-dot-v {
          left: 50%;
          top: 0;
          transform: translate(-50%, -50%);
          animation: kinzy-flow-dot-v 8s ease-in-out infinite;
        }
        @keyframes kinzy-flow-dot {
          0% { left: 0%; opacity: 0; }
          8% { opacity: 1; }
          92% { opacity: 1; }
          100% { left: 100%; opacity: 0; }
        }
        @keyframes kinzy-flow-dot-v {
          0% { top: 0%; opacity: 0; }
          8% { opacity: 1; }
          92% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .kinzy-flow-dot,
          .kinzy-flow-dot-v {
            animation: none;
            opacity: 0;
          }
        }
      `}</style>
    </Section>
  );
}

function SectionDecor() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div
        className="absolute inset-0 opacity-50"
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(158 40% 72% / 0.05) 1px, transparent 1px), linear-gradient(to bottom, hsl(158 40% 72% / 0.05) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
        }}
      />
      <svg className="absolute -right-8 top-0 h-full w-[40%] text-primary" viewBox="0 0 480 420" fill="none">
        <path d="M30 390C160 310 200 170 450 40" stroke="currentColor" strokeOpacity="0.16" />
        <path d="M80 420C210 320 260 150 470 100" stroke="currentColor" strokeOpacity="0.1" />
        <circle cx="450" cy="40" r="3" fill="currentColor" fillOpacity="0.55" />
        <circle cx="300" cy="188" r="2.5" fill="currentColor" fillOpacity="0.4" />
      </svg>
    </div>
  );
}

const whyPointIcons = {
  target: Target,
  heart: Heart,
  users: Users,
};

const outcomeChip = {
  border: '1px solid hsl(164 20% 90%)',
  backgroundColor: 'hsl(40 20% 99%)',
  boxShadow: '0 4px 12px hsl(165 37% 22% / 0.04)',
};
const outcomeChipOn = {
  border: '1px solid hsl(165 37% 22% / 0.28)',
  backgroundColor: 'hsl(158 42% 95%)',
  boxShadow: '0 8px 18px hsl(165 37% 22% / 0.08)',
};

const paceSteps = [
  { label: 'Role kickoff', grow: 0.72 },
  { label: 'Source', grow: 0.78 },
  { label: 'Screen', grow: 0.86 },
  { label: 'Assess', grow: 0.94 },
];
const routineTasks = ['Source candidates', 'Screen & score', 'Schedule interviews', 'Track follow-ups'];
const shortlistProfiles = [
  { initials: 'BD', role: 'Brand designer', chips: ['Culture', 'Craft'], selected: true },
  { initials: 'PP', role: 'People partner', chips: ['Attitude', 'Culture'], selected: true },
  { initials: 'SL', role: 'Store lead', chips: ['Skills', 'Fit'], selected: false },
];

function OutcomeBridge({ index }) {
  return (
    <span className="kinzy-bridge pointer-events-none absolute right-0 top-1/2 hidden w-5 translate-x-full -translate-y-1/2 md:block" style={{ '--bridge': index }} aria-hidden="true">
      <span className="kinzy-bridge-line absolute inset-x-0 top-1/2 h-[2px]" style={{ marginTop: '-1px', backgroundColor: 'hsl(165 37% 22% / 0.34)' }} />
      <span className="kinzy-bridge-dot absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full" />
      <span className="kinzy-bridge-run" />
    </span>
  );
}

function OutcomeCard({ index, icon: Icon, title, body, children, last, accent }) {
  return (
    <li className="kinzy-metric relative flex cursor-default flex-col rounded-[20px] px-5 py-5 sm:px-6" style={{ '--cs-i': index, backgroundColor: accent }}>
      <span className="kinzy-metric-sheen" aria-hidden="true" />
      <div className="flex items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon className="kinzy-metric-icon h-4 w-4" aria-hidden="true" />
        </span>
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
      </div>
      <p className="mt-2 min-h-[2.7rem] text-sm leading-snug text-muted-foreground">{body}</p>
      <div className="kinzy-outcome-art mt-3 flex h-[188px] items-center">{children}</div>
      {last ? null : <OutcomeBridge index={index} />}
    </li>
  );
}

export function HiringSpeedArt({ steps = paceSteps, finalLabel = 'Qualified shortlist' }) {
  return (
    <div className="relative flex h-full w-full items-center" aria-hidden="true">
      <div className="relative flex w-full items-end">
        <svg className="pointer-events-none absolute inset-x-0 bottom-1 h-14 w-full" viewBox="0 0 360 56" preserveAspectRatio="none" fill="none">
          <path className="kinzy-speed-curve" d="M4 48C78 48 96 34 168 24C236 15 286 10 356 6" />
        </svg>
        <div className="relative flex w-full items-end gap-1.5">
          {steps.map((step) => (
            <span
              key={step.label}
              className="flex min-w-0 flex-col items-center justify-center gap-1 rounded-lg px-1 py-1.5 text-center"
              style={{ ...outcomeChip, flexGrow: step.grow, flexBasis: 0 }}
            >
              <span className="text-[9px] font-medium leading-tight text-foreground">{step.label}</span>
              {step.status ? <span className="text-[8px] font-medium leading-none text-primary">{step.status}</span> : null}
            </span>
          ))}
          <span className="kinzy-final-step flex min-w-[88px] flex-[1.4] flex-col items-center gap-1.5 rounded-xl px-2 py-3 text-center" style={outcomeChipOn}>
            <span className="text-[11px] font-semibold leading-tight text-foreground">{finalLabel}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

export function TimeSavingArt({
  tasks = routineTasks,
  hub = 'AI automation',
  resultTitle = 'Recruiter',
  resultMeta = 'Conversation',
  resultBadge = 'Focus',
}) {
  return (
    <div className="grid h-full w-full grid-cols-[minmax(0,0.92fr)_30px_minmax(68px,0.72fr)_14px_minmax(0,1fr)] items-center" aria-hidden="true">
      <ul className="space-y-1">
        {tasks.map((task) => (
          <li key={task} className="truncate rounded-md px-1.5 py-0.5 text-[9px] font-medium leading-tight text-muted-foreground" style={outcomeChip}>
            {task}
          </li>
        ))}
      </ul>
      <svg className="h-[108px] w-[30px]" viewBox="0 0 30 108" fill="none">
        <path className="kinzy-merge-line" d="M0 8C16 8 16 54 30 54" stroke="hsl(165 37% 22%)" />
        <path className="kinzy-merge-line" d="M0 34C14 34 16 54 30 54" stroke="hsl(165 37% 22%)" />
        <path className="kinzy-merge-line" d="M0 74C14 74 16 54 30 54" stroke="hsl(165 37% 22%)" />
        <path className="kinzy-merge-line" d="M0 100C16 100 16 54 30 54" stroke="hsl(165 37% 22%)" />
      </svg>
      <div className="flex flex-col items-center gap-1 rounded-xl px-1.5 py-2.5 text-center" style={outcomeChipOn}>
        <Zap className="h-3.5 w-3.5 text-primary" />
        <span className="text-[10px] font-semibold leading-tight text-primary">{hub}</span>
      </div>
      <ArrowRight className="kinzy-auto-arrow h-3.5 w-3.5 text-primary" />
      <div className="rounded-xl bg-white px-2.5 py-2.5" style={{ border: '1px solid hsl(165 37% 22% / 0.2)', boxShadow: '0 8px 18px hsl(165 37% 22% / 0.08)' }}>
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Users className="h-3.5 w-3.5" />
          </span>
          <span>
            <span className="block text-[11px] font-semibold leading-tight text-foreground">{resultTitle}</span>
            <span className="block text-[9px] leading-tight text-muted-foreground">{resultMeta}</span>
          </span>
        </div>
        <span className="mt-2 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-medium text-primary" style={{ backgroundColor: 'hsl(158 42% 94%)' }}>
          {resultBadge}
        </span>
      </div>
    </div>
  );
}

export function QualifiedShortlistArt({ profiles = shortlistProfiles, panelTitle = 'Qualified shortlist' }) {
  const selected = profiles.filter((profile) => profile.selected);
  return (
    <div className="grid h-full w-full grid-cols-[minmax(0,1.2fr)_18px_minmax(0,0.86fr)] items-center" aria-hidden="true">
      <ul className="space-y-1.5">
        {profiles.map((profile) => (
          <li
            key={profile.initials}
            className={`flex items-center gap-1.5 rounded-xl px-1.5 py-1.5 ${profile.selected ? 'kinzy-pick' : 'opacity-55'}`}
            style={profile.selected ? outcomeChipOn : outcomeChip}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[9px] font-semibold text-primary">
              {profile.initials}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[10px] font-semibold leading-tight text-foreground">{profile.role}</span>
              <span className="mt-1 flex gap-1">
                {profile.chips.map((chip) => (
                  <span key={chip} className="rounded-full bg-white px-1.5 py-0.5 text-[8px] font-medium text-primary">
                    {chip}
                  </span>
                ))}
              </span>
            </span>
          </li>
        ))}
      </ul>
      <svg className="h-[120px] w-[18px]" viewBox="0 0 18 120" fill="none">
        <path className="kinzy-merge-line" d="M0 22C10 22 10 42 18 42" stroke="hsl(165 37% 22%)" />
        <path className="kinzy-merge-line" d="M0 62C10 62 10 78 18 78" stroke="hsl(165 37% 22%)" />
      </svg>
      <div className="rounded-xl px-2 py-2" style={outcomeChipOn}>
        <p className="text-[10px] font-semibold text-primary">{panelTitle}</p>
        <ul className="mt-1.5 space-y-1">
          {selected.map((profile) => (
            <li key={profile.initials} className="flex items-center gap-1.5 rounded-lg bg-white px-1.5 py-1">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[8px] font-semibold text-primary">
                {profile.initials}
              </span>
              <span className="min-w-0 flex-1 truncate text-[9px] font-medium text-foreground">{profile.role}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function KinzyResult({ study, metricState }) {
  const panelRef = useRef(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const node = panelRef.current;
    if (!node) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setActive(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setActive(true);
        observer.disconnect();
      }
    }, { threshold: 0.2 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [study.slug]);
  return (
    <section className="relative overflow-hidden border-b border-border">
      <SectionDecor />
      <div className="relative mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8 lg:py-14">
        <SectionLabel>The result</SectionLabel>
        <h2 className="mt-3 max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">
          {study.resultsTitle}
        </h2>
        <p className="mt-3 max-w-xl text-base leading-relaxed text-muted-foreground">{study.resultsIntro}</p>
        {study.slug === 'kinzy-club' && metricState.demo ? (
          <p className="mt-4 inline-flex rounded-full border border-primary/25 px-3 py-1 text-xs font-medium text-primary">
            Illustrative outcomes · Pending customer approval
          </p>
        ) : null}
        {study.slug === 'kinzy-club' ? (
          <ul ref={panelRef} className={`kinzy-metrics mt-8 grid grid-cols-1 items-stretch gap-3 md:grid-cols-3 md:gap-5 ${active ? 'is-active' : ''}`}>
            <OutcomeCard index={0} icon={Zap} title="Hiring speed" body="Go from role kickoff to qualified shortlist faster." accent="hsl(158 45% 98%)">
              <HiringSpeedArt />
            </OutcomeCard>
            <OutcomeCard index={1} icon={Clock} title="Time saving" body="Automate routine work so recruiters can focus on high-value conversations." accent="hsl(164 32% 97.5%)">
              <TimeSavingArt />
            </OutcomeCard>
            <OutcomeCard index={2} icon={Users} title="Qualified shortlist" body="AI-powered screening with human review to surface the right candidates." accent="hsl(152 34% 97%)" last>
              <QualifiedShortlistArt />
            </OutcomeCard>
          </ul>
        ) : study.outcomeStory ? (
          <ul ref={panelRef} className={`kinzy-metrics mt-8 grid grid-cols-1 items-stretch gap-3 md:grid-cols-3 md:gap-5 ${active ? 'is-active' : ''}`}>
            {study.outcomeStory.map((card, index) => {
              const Icon = card.icon === 'clock' ? Clock : card.icon === 'users' ? Users : Zap;
              return (
                <OutcomeCard key={card.title} index={index} icon={Icon} title={card.title} body={card.body} accent={card.accent} last={index === study.outcomeStory.length - 1}>
                  {card.kind === 'merge' ? (
                    <TimeSavingArt tasks={card.tasks} hub={card.hub} resultTitle={card.resultTitle} resultMeta={card.resultMeta} resultBadge={card.resultBadge} />
                  ) : card.kind === 'shortlist' ? (
                    <QualifiedShortlistArt profiles={card.profiles} panelTitle={card.panel} />
                  ) : (
                    <HiringSpeedArt steps={card.steps} finalLabel={card.final} />
                  )}
                </OutcomeCard>
              );
            })}
          </ul>
        ) : null}
        <style>{`
          .kinzy-speed-curve {
            stroke: hsl(165 37% 22%);
            stroke-width: 1.75;
            stroke-linecap: round;
            stroke-dasharray: 6 7;
            stroke-opacity: 0.55;
            vector-effect: non-scaling-stroke;
          }
          .kinzy-merge-line { fill: none; stroke-width: 1.25; stroke-opacity: 0.45; stroke-linecap: round; }
          .kinzy-metric { transition: transform 200ms ease, box-shadow 200ms ease; }
          .kinzy-bridge-line { transform: scaleX(0); transform-origin: left center; }
          .kinzy-metrics.is-active .kinzy-bridge-line { transform: scaleX(1); transition: transform 700ms ease; }
          @media (hover: hover) and (pointer: fine) {
            .kinzy-metric:hover { transform: translateY(-2px); }
            .kinzy-metric:hover .kinzy-metric-icon { transform: scale(1.03); }
            .kinzy-metric:hover .kinzy-pick { transform: translateX(5px); }
          }
          @media (prefers-reduced-motion: reduce) {
            .kinzy-metric, .kinzy-pick, .kinzy-metric-icon, .kinzy-bridge-line { transition: none; animation: none; transform: none; }
          }
        `}</style>
        <p className="mt-8 text-xs font-semibold uppercase tracking-wider text-primary">What the customer valued</p>
        <ul className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          {study.resultEvidence.map((item) => {
            const quoted = item.kind === 'quote';
            return (
              <li
                key={item.label}
                className={`rounded-2xl border px-5 py-4 ${
                  quoted
                    ? 'border-primary/25 bg-secondary'
                    : 'border-border bg-card shadow-sm'
                }`}
              >
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                  {quoted ? <Quote className="h-3.5 w-3.5" aria-hidden="true" /> : null}
                  {item.label}
                </p>
                <p className="mt-2 text-sm font-medium leading-snug text-foreground">{item.body}</p>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function KinzyWhy({ study }) {
  const points = study.why.points || [];
  return (
    <section className="cs-reveal relative overflow-hidden border-b border-border">
      <SectionDecor />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-8 px-4 py-10 sm:px-6 sm:py-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-12 lg:px-8 lg:py-14">
        <div className="min-w-0">
          <SectionLabel>Why it matters</SectionLabel>
          <h2 className="mt-3 max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">
            {study.why.title}
          </h2>
          <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">{study.why.body}</p>
        </div>
        {points.length > 0 ? (
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">What the customer valued</p>
          <div className="relative mt-4">
            <span
              className="absolute left-[12%] right-[12%] top-[15px] hidden h-px md:block"
              style={{ backgroundColor: 'hsl(158 40% 72% / 0.4)' }}
              aria-hidden="true"
            />
            <ul className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {points.map((point, index) => {
                const Icon = whyPointIcons[point.icon] || Target;
                return (
                  <li key={point.label} className="cs-stagger relative min-w-0" style={{ '--cs-i': index }}>
                    <span
                      className="absolute left-1/2 top-[11px] hidden h-2 w-2 -translate-x-1/2 rounded-full md:block"
                      style={{ backgroundColor: 'hsl(165 37% 22%)' }}
                      aria-hidden="true"
                    />
                    <div className="flex h-full flex-col rounded-xl border border-primary/20 bg-card shadow-sm px-4 pb-4 pt-8 md:pt-8">
                      <Icon className="h-4 w-4 text-primary" aria-hidden="true" />
                      <p className="mt-3 text-sm font-semibold text-foreground">{point.label}</p>
                      {point.body ? (
                        <p className="mt-1.5 text-sm leading-snug text-muted-foreground">{point.body}</p>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
        ) : null}
      </div>
    </section>
  );
}

function splitTestimonial(quote) {
  const text = String(quote || '').trim();
  const match = text.match(/^(.+?[.!?])\s+([\s\S]+)$/);
  if (!match) return { lead: text, rest: '' };
  return { lead: match[1], rest: match[2].trim() };
}

function reviewerInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return '';
  return parts.slice(0, 2).map((part) => part[0]).join('').toUpperCase();
}

function KinzyVoice({ study }) {
  if (!study.useLegacyCustomerVoice) return <CustomerInsights study={study} />;
  const { lead, rest } = splitTestimonial(study.quote);
  const name = study.quoteBy || '';
  const role = study.quoteRole || study.attribution || '';
  const initials = reviewerInitials(name);
  const dotId = `voice-dots-${study.slug}`;

  return (
    <section className="cs-reveal relative overflow-hidden border-b border-border">
      <div className="relative mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-14">
        <div className="flex items-center gap-4">
          <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Customer insights</h2>
          <span className="h-px w-14 bg-primary/35" aria-hidden="true" />
        </div>
        <figure
          className="mt-6 grid grid-cols-1 overflow-hidden rounded-[28px] border lg:grid-cols-[minmax(0,1.85fr)_minmax(200px,0.88fr)]"
          style={{
            backgroundColor: 'hsl(158 45% 98%)',
            borderColor: 'hsl(158 28% 82%)',
            boxShadow: '0 10px 32px hsl(165 37% 22% / 0.05)',
          }}
        >
          <div className="min-w-0 px-6 pb-5 pt-6 sm:px-10 sm:pb-6 sm:pt-8 lg:px-12 lg:pb-5 lg:pt-9">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary" aria-hidden="true">
              <Quote className="h-4 w-4" />
            </span>
            <blockquote className="mt-5">
              <p className="max-w-3xl text-xl font-bold leading-snug tracking-[-0.02em] text-foreground sm:text-2xl">
                “{lead}”
              </p>
              {rest ? (
                <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted-foreground sm:text-[15px] sm:leading-7">
                  “{rest}”
                </p>
              ) : null}
            </blockquote>
            <div className="mt-6 h-px w-full bg-primary/15" aria-hidden="true" />
            <figcaption className="mt-5 flex items-center gap-3">
              {initials ? (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold tracking-wide text-primary">
                  {initials}
                </span>
              ) : null}
              <span className="min-w-0">
                {name ? <span className="block text-sm font-bold text-foreground">{name}</span> : null}
                {role ? (
                  <span className="mt-0.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-primary">
                    {role}
                  </span>
                ) : null}
              </span>
            </figcaption>
          </div>
          <div className="relative min-h-[180px] overflow-hidden lg:min-h-full" aria-hidden="true">
            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 420 480" preserveAspectRatio="xMaxYMid slice">
              <defs>
                <pattern id={dotId} width="16" height="16" patternUnits="userSpaceOnUse">
                  <circle cx="1.2" cy="1.2" r="0.9" fill="hsl(165 37% 22%)" opacity="0.28" />
                </pattern>
              </defs>
              <circle cx="360" cy="70" r="108" fill="hsl(158 42% 78%)" opacity="0.22" />
              <circle cx="430" cy="190" r="150" fill="hsl(164 36% 84%)" opacity="0.28" />
              <circle cx="300" cy="250" r="86" fill="hsl(158 32% 90%)" opacity="0.55" />
              <path d="M30 400C150 320 230 230 410 150" fill="none" stroke="hsl(165 37% 22%)" strokeOpacity="0.28" />
              <path d="M70 450C190 350 260 270 430 210" fill="none" stroke="hsl(165 37% 22%)" strokeOpacity="0.16" />
              <circle cx="230" cy="230" r="4" fill="hsl(165 37% 22%)" />
              <circle cx="352" cy="168" r="3.5" fill="hsl(165 37% 22%)" />
              <rect x="268" y="16" width="136" height="92" fill={`url(#${dotId})`} />
            </svg>
          </div>
        </figure>
      </div>
    </section>
  );
}

function SectionLabel({ children }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wider text-primary">{children}</p>
  );
}

function SectionTitle({ children, className = '' }) {
  return (
    <h2 className={`mt-3 max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl ${className}`}>
      {children}
    </h2>
  );
}

function SectionBody({ children }) {
  return (
    <p className="mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">{children}</p>
  );
}

function DemoLabel({ customer }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-wider text-primary">
      Product demo · not {customer} candidate data
    </p>
  );
}

function columnClass(count, three) {
  if (count <= 1) return 'grid-cols-1 max-w-xl';
  if (count === 2) return 'grid-cols-1 sm:grid-cols-2';
  if (count === 4) return 'grid-cols-1 sm:grid-cols-2';
  return three;
}

const profileBar = {
  green: 'bg-primary',
  purple: 'bg-violet-500',
  orange: 'bg-orange-500',
};

const profileIcon = {
  green: 'text-primary',
  purple: 'text-violet-600',
  orange: 'text-orange-500',
  blue: 'text-sky-600',
};

function ProfileMetricRows({ rows, tone }) {
  return (
    <ul className="mt-2.5 flex flex-1 flex-col justify-around gap-2">
      {rows.map((row) => (
        <li key={row.label} className="flex items-center gap-2">
          <span className="w-[46%] shrink-0 text-[11px] leading-tight text-foreground/80">{row.label}</span>
          <span className="h-1.5 min-w-[2rem] flex-1 overflow-hidden rounded-full bg-slate-200">
            <span className={`block h-full rounded-full ${profileBar[tone]}`} style={{ width: `${row.percent}%` }} />
          </span>
          <span className="w-10 shrink-0 text-right text-[11px] font-medium tabular-nums text-foreground">{row.value}</span>
        </li>
      ))}
    </ul>
  );
}

function ProfileCard({ title, icon: Icon, tone, children }) {
  return (
    <section className="flex h-full flex-col rounded-xl border border-slate-200 bg-slate-50 p-3">
      <h3 className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
        <Icon className={`h-3.5 w-3.5 shrink-0 ${profileIcon[tone]}`} aria-hidden="true" />
        {title}
      </h3>
      {children}
    </section>
  );
}

function IllustrativeProfile({ profile }) {
  return (
    <div className="flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border border-border bg-background text-foreground shadow-sm">
      <div className="flex items-start justify-between gap-3 px-3 py-3 sm:px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
            {profile.initials}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold leading-tight">{profile.name}</p>
            <p className="mt-0.5 text-xs leading-tight text-muted-foreground">{profile.role}</p>
            <p className="mt-1 text-[11px] leading-tight text-muted-foreground">{profile.experience}</p>
          </div>
        </div>
        <p className="shrink-0 rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-semibold leading-tight text-emerald-800">
          {profile.status}
        </p>
      </div>
      <div className="flex min-w-0 gap-3 overflow-x-auto border-y border-border px-3 text-[11px] [scrollbar-width:none] sm:px-4 [&::-webkit-scrollbar]:hidden">
        {profile.tabs.map((tab) => (
          <span
            key={tab}
            className={
              tab === profile.activeTab
                ? 'shrink-0 border-b-2 border-primary py-2 font-semibold text-primary'
                : 'shrink-0 py-2 text-muted-foreground'
            }
          >
            {tab}
          </span>
        ))}
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-2 p-2 sm:grid-cols-2 sm:p-3">
        <ProfileCard title="Skills Alignment" icon={Target} tone="green">
          <ProfileMetricRows rows={profile.skills} tone="green" />
        </ProfileCard>
        <ProfileCard title="Assessment & Screening" icon={ClipboardCheck} tone="blue">
          <ul className="mt-2.5 flex flex-1 flex-col justify-around gap-2">
            {profile.assessments.map((item) => (
              <li key={item.label} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 text-[11px] leading-tight text-foreground/80">{item.label}</span>
                <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
                  {item.status}
                </span>
              </li>
            ))}
          </ul>
        </ProfileCard>
      </div>
    </div>
  );
}

function KinzyHero({ study }) {
  const shot = assetSrc(study, 'heroScreenshot');
  const frameRef = usePointerNudge();

  return (
    <section className="cs-reveal relative overflow-hidden border-b border-border">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 top-8 h-80 w-80 rounded-full bg-primary/10 blur-3xl"
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-[minmax(0,1fr)] items-start gap-8 px-4 py-8 sm:gap-10 sm:px-6 lg:grid-cols-[minmax(0,1.04fr)_minmax(0,0.96fr)] lg:gap-12 lg:px-8 lg:py-10">
        <div className="min-w-0 self-start">
          <p className="inline-flex rounded-full bg-primary/[0.07] px-3 py-1.5 text-xs font-semibold uppercase tracking-normal text-primary">
            {study.category}
          </p>
          <div className="mt-5 flex items-center gap-3 sm:gap-4">
            <div className="flex h-10 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-white px-2 py-1 shadow-sm">
              <img src={study.assets.logo} alt="" className="h-8 w-auto max-w-none scale-125 object-contain" />
            </div>
            <p className="text-2xl font-bold leading-tight tracking-[-0.03em] text-foreground sm:text-3xl">
              {study.customer}
            </p>
          </div>
          <h1 className="mt-5 max-w-3xl text-balance text-3xl font-bold leading-[1.08] tracking-[-0.02em] text-foreground sm:text-4xl lg:text-[2.5rem]">
            {study.headline}
          </h1>
          <p className="mt-4 max-w-3xl text-lg leading-relaxed text-muted-foreground">
            {study.context}
          </p>
          <ul className="mt-6 flex flex-wrap gap-3" aria-label="Case study focus areas">
            {[
              { label: 'Culture Fit', Icon: Users },
              { label: 'Skills + Attitude', Icon: Star },
              { label: 'Growing Team', Icon: TrendingUp },
            ].map(({ label, Icon }) => (
              <li key={label} className="inline-flex items-center gap-2 rounded-full bg-primary/[0.07] px-4 py-3 text-sm font-medium text-primary sm:px-5 sm:text-base">
                <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span className="whitespace-nowrap">{label}</span>
              </li>
            ))}
          </ul>
        </div>

        <figure ref={frameRef} className="kinzy-frame flex w-full min-w-0 flex-col self-start overflow-hidden rounded-3xl border border-border bg-card p-3 shadow-sm sm:p-4">
          {shot ? (
            <img
              src={shot}
              alt="Illustrative SearchJob.ai candidate evaluation view."
              className="kinzy-shot w-full rounded-2xl"
            />
          ) : study.illustrativeProfile ? (
            <div className="kinzy-shot min-h-0 flex-1">
              <IllustrativeProfile profile={study.illustrativeProfile} />
            </div>
          ) : study.heroVisual ? (
            <div className="kinzy-shot rounded-2xl border border-border px-4 py-4">
              {study.heroVisual.badge ? (
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">{study.heroVisual.badge}</p>
              ) : null}
              <p className="text-sm font-semibold text-foreground">{study.heroVisual.title}</p>
              {study.heroVisual.description ? (
                <p className="mt-2 text-sm leading-snug text-muted-foreground">{study.heroVisual.description}</p>
              ) : null}
              {study.heroVisual.items?.length ? (
                <ul className="mt-4 divide-y divide-white/10 border-t border-border">
                  {study.heroVisual.items.map((item) => (
                    <li key={item} className="py-2.5 text-sm font-medium text-foreground">{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </figure>
      </div>
    </section>
  );
}

const proofIcons = {
  target: Target,
  heart: Heart,
  users: Users,
  clipboard: ClipboardCheck,
  check: Briefcase,
  briefcase: Briefcase,
  qualification: GraduationCap,
};

function proofPanels(study) {
  if (study.productProof) return study.productProof;
  return (study.visuals?.panels || []).map((panel) => ({
    title: panel.title,
    body: panel.intro || (panel.blocks || []).map((block) => block.body).filter(Boolean).join(' '),
    imageKey: panel.imageKey,
    rows: panel.items
      ? panel.items.map((label) => ({ label, icon: 'target' }))
      : (panel.blocks || []).map((block) => ({ label: block.kicker || block.body, icon: 'clipboard' })),
  }));
}

function ProductProof({ study }) {
  const panels = proofPanels(study);
  if (!panels.length) return null;
  const title = study.productProofTitle || study.visuals?.heading;

  return (
    <Section className="py-8 sm:py-10 lg:py-12">
      <SectionLabel>Technology</SectionLabel>
      <SectionTitle>{title}</SectionTitle>
      <ul className={`mt-5 grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 ${panels.length > 2 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
        {panels.map((panel, index) => {
          const src = assetSrc(study, panel.imageKey);
          return (
            <li key={panel.title} className="cs-stagger min-w-0" style={{ '--cs-i': index }}>
              <figure className="flex h-full min-w-0 flex-col rounded-2xl border border-border bg-card shadow-sm p-4">
                <figcaption>
                  <h3 className="text-base font-semibold text-foreground">{panel.title}</h3>
                  <p className="mt-1.5 min-h-10 text-sm leading-snug text-muted-foreground">{panel.body}</p>
                </figcaption>
                {src ? (
                  <img src={src} alt="" className="mt-3 w-full rounded-xl" />
                ) : (
                  <div className={`mt-3 flex min-h-0 flex-1 ${panel.kind === 'intelligence' ? 'gap-3' : ''}`}>
                    {panel.kind === 'intelligence' ? (
                      <span className="w-px shrink-0 bg-primary/25" aria-hidden="true" />
                    ) : null}
                    <ul className="min-w-0 flex-1 divide-y divide-white/10 border-t border-border">
                      {panel.rows.map((row) => {
                        const Icon = proofIcons[row.icon];
                        return (
                          <li key={row.label} className="flex min-h-11 items-center gap-2.5">
                            {Icon ? (
                              <Icon className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                            ) : null}
                            <span className="min-w-0 text-sm font-medium text-foreground">{row.label}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </figure>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

function VisualSlot({ study, imageKey, items, blocks, alt }) {
  const src = assetSrc(study, imageKey);
  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        className="mt-5 w-full rounded-2xl border border-border"
      />
    );
  }
  return <CaseStudyDemo items={items} blocks={blocks} />;
}

export default function CaseStudyPage({ study }) {
  useSEO(study.seo);
  const metricState = caseStudyMetrics(study);
  const tight = Boolean(study.glanceCompact);
  const glanceCols = columnClass(study.glance.length, 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3');
  const painCols = columnClass(study.painPoints.length, 'grid-cols-1 sm:grid-cols-3');
  const featureCols =
    (study.features?.length || 0) >= 4 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2';

  if (study.slug === 'grand-gaming' || study.slug === 'manest-ai') {
    const Study = study.slug === 'grand-gaming' ? GrandGamingStudy : ManestStudy;
    return (
      <div className="relative min-h-screen bg-background text-foreground">
        <Header solid />
        <main className="relative pt-16">
          <Study study={study} />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <Header solid />

      <main className="relative pt-16">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[560px] overflow-hidden" aria-hidden="true">
          <div className="absolute left-[8%] top-[-20%] h-[70%] w-[55%] rounded-full bg-primary/15 blur-3xl" />
          <div className="absolute right-[5%] top-0 h-[55%] w-[45%] rounded-full bg-emerald-500/10 blur-3xl" />
        </div>
        {study.slug === 'kinzy-club' ? (
          <KinzyHero study={study} />
        ) : study.heroVariant === 'surge-pipeline' ? (
          <GrandGamingHero study={study} />
        ) : (
        <section className="border-b border-border">
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:px-8 lg:py-24">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                {study.category}
              </p>
              <p className="mt-4 text-5xl font-bold tracking-[-0.03em] text-foreground sm:text-6xl lg:text-7xl">
                {study.customer}
              </p>
              <h1 className="mt-6 max-w-xl text-3xl font-semibold leading-tight tracking-[-0.02em] text-foreground sm:text-4xl">
                {study.headline}
              </h1>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                {study.context}
              </p>
            </div>

            <figure className="rounded-3xl border border-border bg-card shadow-sm p-6 sm:p-8">
              <div className="flex h-28 items-center justify-center overflow-hidden rounded-2xl bg-white sm:h-32">
                <img src={study.assets.logo} alt={study.customer} className={study.assets.logoClassName} />
              </div>
              <figcaption className="mt-6">
                <DemoLabel customer={study.customer} />
                <p className="mt-3 text-lg font-semibold text-foreground">{study.heroVisual.title}</p>
                {study.heroVisual.badge ? (
                  <p className="mt-3 inline-flex rounded-full border border-[hsl(158_45%_48%)]/60 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
                    {study.heroVisual.badge}
                  </p>
                ) : null}
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {study.heroVisual.description}
                </p>
                <VisualSlot
                  study={study}
                  imageKey={study.heroVisual.imageKey}
                  items={study.heroVisual.items}
                  alt={`${study.heroVisual.title}. Product demo, not ${study.customer} candidate data.`}
                />
              </figcaption>
            </figure>
          </div>
        </section>
        )}

        <Section compact={study.glanceCompact}>
          <SectionLabel>Customer at a glance</SectionLabel>
          <SectionTitle>Executive snapshot</SectionTitle>
          <dl
            className={
              study.glanceCompact
                ? `mt-6 grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 ${
                    study.glance.length > 3 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'
                  }`
                : `mt-8 grid gap-4 ${glanceCols}`
            }
          >
            {study.glance.map(([label, value], index) => (
              <div
                key={label}
                className={`cs-stagger ${
                  study.glanceCompact
                    ? 'flex h-full flex-col rounded-2xl border border-border bg-card shadow-sm px-5 py-4'
                    : 'h-full rounded-2xl border border-border bg-card shadow-sm p-6'
                }`}
                style={{ '--cs-i': index }}
              >
                <dt className="text-xs font-semibold uppercase tracking-wider text-primary">{label}</dt>
                <dd
                  className={
                    study.glanceCompact
                      ? 'mt-2 text-base font-medium leading-snug text-foreground'
                      : 'mt-3 text-lg font-semibold leading-snug text-foreground'
                  }
                >
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </Section>

        <CaseStudyChallenge study={study} />

        {study.beforeBalance?.length ? (
          <Section className="py-8 sm:py-10 lg:py-12">
            <SectionLabel>Before SearchJob.ai</SectionLabel>
            <h2 className="mt-3 max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">
              {`What ${study.customer} needed to balance`}
            </h2>
            <ul className={`mt-5 grid grid-cols-1 items-stretch gap-3 lg:gap-4 ${study.beforeBalance.length > 2 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
              {study.beforeBalance.map((item, index) => (
                <li key={item.label} className="min-w-0">
                  <div
                    className="kinzy-balance-card flex h-full flex-col rounded-2xl border border-border bg-card shadow-sm px-4 py-3.5"
                    style={{ animationDelay: `${index * 2.2}s` }}
                  >
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {item.label}
                    </p>
                    <p className="mt-1.5 text-sm font-medium leading-snug text-foreground">{item.body}</p>
                  </div>
                </li>
              ))}
            </ul>
            <style>{`
              .kinzy-balance-card {
                transition: transform 200ms ease, border-color 200ms ease, box-shadow 200ms ease;
              }
              .kinzy-balance-card:not(:hover) {
                animation: kinzy-balance-pass 6.6s ease-in-out infinite;
              }
              .kinzy-balance-card:hover {
                transform: translateY(-2px);
                border-color: hsl(165 37% 22% / 0.35);
                box-shadow: 0 8px 24px hsl(165 37% 22% / 0.08);
              }
              @keyframes kinzy-balance-pass {
                0%, 22%, 100% {
                  border-color: hsl(164 20% 90%);
                  box-shadow: none;
                  transform: none;
                }
                10% {
                  border-color: hsl(165 37% 22% / 0.35);
                  box-shadow: 0 8px 24px hsl(165 37% 22% / 0.08);
                  transform: translateY(-2px);
                }
              }
              @media (prefers-reduced-motion: reduce) {
                .kinzy-balance-card {
                  animation: none;
                }
              }
            `}</style>
          </Section>
        ) : null}

        <KinzyApproach study={study} />

        <KinzyWorkflow study={study} />
        {false ? (
        <Section compact={tight}>
          <SectionLabel>How the workflow changed</SectionLabel>
          <SectionTitle>{study.workflowTitle}</SectionTitle>
          <ol className="mt-8 flex flex-col gap-3 lg:flex-row lg:items-stretch">
            {study.workflow.map((step, index) => {
              const differentiated = step === study.workflowHighlight;
              return (
                <li key={step} className="flex min-w-0 flex-1 flex-col gap-3 lg:flex-row lg:items-center">
                  <div
                    className={`flex min-h-[5.5rem] min-w-0 flex-1 flex-col justify-center rounded-2xl border px-4 py-5 ${
                      differentiated
                        ? 'border-primary/30 bg-secondary'
                        : 'border-border bg-card shadow-sm'
                    }`}
                  >
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                      {index + 1}
                    </span>
                    <span className="mt-2 text-base font-semibold leading-snug text-foreground">{step}</span>
                  </div>
                  {index < study.workflow.length - 1 ? (
                    <span className="flex items-center justify-center text-primary" aria-hidden="true">
                      <ArrowDown className="h-4 w-4 lg:hidden" />
                      <ArrowRight className="hidden h-4 w-4 lg:block" />
                    </span>
                  ) : (
                    <span className="hidden w-4 shrink-0 lg:block" aria-hidden="true" />
                  )}
                </li>
              );
            })}
          </ol>
          {study.workflowNote?.body ? (
            <div className="mt-8 max-w-3xl rounded-2xl border border-primary/20 bg-secondary p-6">
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                {study.workflowNote.label}
              </p>
              <p className="mt-3 text-base leading-relaxed text-foreground sm:text-lg">{study.workflowNote.body}</p>
            </div>
          ) : study.workflowNote?.label ? (
            <p className="mt-6 text-sm font-semibold uppercase tracking-wider text-primary">
              {study.workflowNote.label}
            </p>
          ) : null}
        </Section>
        ) : null}

        {(study.productProof || study.visuals?.panels?.length) ? (
          <ProductProof study={study} />
        ) : (
        <>
        <Section>
          <SectionLabel>Technology</SectionLabel>
          <SectionTitle>Features this story supports</SectionTitle>
          <ul className={`mt-8 grid gap-4 ${featureCols}`}>
            {study.features.map((item) => (
              <li key={item} className="rounded-2xl border border-border p-6 text-base font-semibold leading-snug text-foreground">
                {item}
              </li>
            ))}
          </ul>
        </Section>

        <Section>
          <SectionLabel>Product visuals</SectionLabel>
          <SectionTitle>{study.visuals.heading}</SectionTitle>
          <SectionBody>{study.visuals.intro}</SectionBody>
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            {study.visuals.panels.map((panel) => (
              <figure key={panel.title} className="flex h-full flex-col rounded-3xl border border-border bg-card shadow-sm p-6">
                <DemoLabel customer={study.customer} />
                <figcaption className="mt-4 text-xl font-semibold text-foreground">{panel.title}</figcaption>
                {panel.intro ? (
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{panel.intro}</p>
                ) : null}
                <VisualSlot
                  study={study}
                  imageKey={panel.imageKey}
                  items={panel.items}
                  blocks={panel.blocks}
                  alt={`${panel.title}. Product demo, not ${study.customer} candidate data.`}
                />
              </figure>
            ))}
          </div>
        </Section>
        </>
        )}

        {study.slug === 'kinzy-club' || study.outcomeStory ? (
          <KinzyResult study={study} metricState={metricState} />
        ) : (
        <Section compact={tight}>
          <SectionLabel>The result</SectionLabel>
          <SectionTitle>{study.resultsTitle || 'What the customer supports'}</SectionTitle>
          {metricState.items.length > 0 ? (
            <>
              {metricState.demo ? (
                <p className="mt-5 text-sm leading-relaxed text-muted-foreground">{DEMO_METRICS_NOTE}</p>
              ) : null}
              <ul className="mt-4 grid grid-cols-1 items-stretch gap-4 sm:grid-cols-3">
                {metricState.items.map((metric) => (
                  <li key={metric.label} className="flex h-full flex-col rounded-2xl border border-border bg-card shadow-sm p-6">
                    <p className="text-4xl font-semibold tracking-tight text-foreground">{metric.value}</p>
                    <p className="mt-2 text-sm font-medium leading-snug text-muted-foreground">{metric.label}</p>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {study.results?.length > 0 ? (
            <ul className={`grid grid-cols-1 gap-4 md:grid-cols-2 ${metricState.items.length > 0 ? 'mt-6' : 'mt-8'} ${study.resultsTitle ? '' : 'xl:grid-cols-3'}`}>
              {study.results.map((result) => (
                <li key={result} className="rounded-2xl border border-border bg-card shadow-sm p-6 text-base leading-relaxed text-foreground">
                  {result}
                </li>
              ))}
            </ul>
          ) : null}
        </Section>
        )}

        {study.slug === 'kinzy-club' ? (
          <KinzyWhy study={study} />
        ) : (
        <Section compact={tight}>
          <SectionLabel>Why it matters</SectionLabel>
          <SectionTitle>{study.why.title}</SectionTitle>
          <SectionBody>{study.why.body}</SectionBody>
        </Section>
        )}

        {study.slug === 'kinzy-club' ? (
          <KinzyVoice study={study} />
        ) : (
        <Section compact={tight}>
          <SectionLabel>Customer insights</SectionLabel>
          <figure
            className={`mt-6 rounded-3xl border border-border bg-card shadow-sm px-6 sm:px-10 ${
              study.quoteBy ? 'py-8 sm:py-10' : 'py-10 sm:py-14'
            }`}
          >
            <blockquote
              className={`max-w-4xl font-medium leading-relaxed text-foreground ${
                study.quoteBy ? 'text-2xl sm:text-3xl' : 'text-xl sm:text-2xl'
              }`}
            >
              “{study.quote}”
            </blockquote>
            <figcaption className="mt-6 text-sm font-semibold uppercase tracking-wider text-primary">
              {study.quoteBy ? (
                <>
                  <span className="block text-base font-semibold normal-case tracking-normal text-foreground">
                    {study.quoteBy}
                  </span>
                  <span className="mt-1 block">{study.quoteRole}</span>
                </>
              ) : (
                study.attribution
              )}
            </figcaption>
          </figure>
        </Section>
        )}

        <section className="border-b border-border">
          <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-12 text-center sm:px-6 lg:px-8">
            <h2 className="max-w-xl text-xl font-normal leading-tight tracking-tight text-foreground sm:text-2xl">
              Want to see what this workflow would look like for your hiring team?
            </h2>
            <a href={CTA_HREF} className={`${primaryButton} mt-6 w-full sm:w-auto`}>
              {CTA_LABEL}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
